import { useState, useMemo } from "react";
import { Code2, Edit3, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import { useDeleteProfileSkillCategory } from "./hooks/useProfileSkills";
import { useCandidateSkills } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import {
  ProfileSkillFormModal,
  type ProfileSkillCategoryGroup,
} from "./components/ProfileSkillFormModal";
import type { ProfileSkillDetail } from "@/interface";

interface SkillsTabProps {
  profileId?: number | string;
}

export default function SkillsTab({ profileId }: SkillsTabProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategoryData, setSelectedCategoryData] =
    useState<ProfileSkillCategoryGroup | null>(null);
  const [deleteTargetCategory, setDeleteTargetCategory] =
    useState<ProfileSkillCategoryGroup | null>(null);

  const { data: user } = useCurrentUser();
  const targetId = profileId || user?.id;

  const { data: candSkillsRes, isLoading: isCandLoading } =
    useCandidateSkills(targetId);
  const deleteCategoryMutation = useDeleteProfileSkillCategory();

  const skills = useMemo(
    () =>
      candSkillsRes?.pages.flatMap((page: any) =>
        Array.isArray(page?.data) ? page.data : page?.data?.results || [],
      ),
    [candSkillsRes],
  );

  const isLoading = isCandLoading && skills?.length === 0;

  const handleOpenAdd = () => {
    setSelectedCategoryData(null);
    setIsModalOpen(true);
  };

  const handleOpenCategoryEdit = (
    categoryId: number | string,
    categoryName: string,
    items: ProfileSkillDetail[],
  ) => {
    setSelectedCategoryData({
      categoryId,
      categoryName,
      skills: items,
    });
    setIsModalOpen(true);
  };

  const handleDeleteCategoryConfirm = async () => {
    if (!deleteTargetCategory?.categoryId) return;
    try {
      await deleteCategoryMutation.mutateAsync(deleteTargetCategory.categoryId);
      setDeleteTargetCategory(null);
    } catch {
      // Handled by mutation
    }
  };

  const capitalize = (str?: string) => {
    if (!str) return "Intermediate";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const groupedSkills = useMemo(() => {
    const groups: {
      [key: string]: {
        categoryId: number | string;
        items: ProfileSkillDetail[];
      };
    } = {};

    skills?.forEach((sk) => {
      const catName = sk.skill_category_name || "Uncategorized";
      const catId = sk.skill_category || "";

      if (!groups[catName]) {
        groups[catName] = { categoryId: catId, items: [] };
      }
      groups[catName].items.push(sk);
    });

    return Object.entries(groups).map(([category, data]) => ({
      category,
      categoryId: data.categoryId,
      items: data.items,
    }));
  }, [skills]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Code2 className="w-5 h-5 text-purple-600 dark:text-purple-400" /> Technical Skills
        </h2>
        <Button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-bold rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Skill Category
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="space-y-8">
          {groupedSkills?.map(({ category, categoryId, items }) => (
            <div key={category} className="group relative">
              {/* Category Header with Edit & Delete options */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[15px] font-bold text-slate-800 dark:text-slate-200">
                  {category}
                </h3>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  <button
                    onClick={() =>
                      handleOpenCategoryEdit(categoryId, category, items)
                    }
                    className="p-1.5 text-slate-400 hover:text-purple-600 bg-slate-50 dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-950/30 rounded transition-colors"
                    title="Edit Skill Category"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() =>
                      setDeleteTargetCategory({
                        categoryId,
                        categoryName: category,
                        skills: items,
                      })
                    }
                    className="p-1.5 text-slate-400 hover:text-red-600 bg-slate-50 dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors"
                    title="Delete Skill Category"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Skills Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((skill: ProfileSkillDetail) => {
                  const skillName = skill.skill_name || "Skill";
                  const level = capitalize(skill.proficiency);
                  const expYears = skill.experience_years;

                  return (
                    <div
                      key={skill.id}
                      className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800 flex items-center justify-between hover:border-purple-200 dark:hover:border-purple-800 transition-colors"
                    >
                      <div>
                        <div className="font-semibold text-slate-900 dark:text-white text-sm">
                          {skillName}
                        </div>
                        {expYears !== undefined && expYears !== null && (
                          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {expYears} {expYears === 1 ? "year" : "years"} exp.
                          </div>
                        )}
                      </div>

                      <span
                        className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider ${
                          level === "Expert"
                            ? "bg-purple-100 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300"
                            : level === "Advanced"
                              ? "bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300"
                              : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
                        }`}
                      >
                        {level}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {groupedSkills.length === 0 && (
            <div className="text-center p-12 text-slate-500 dark:text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900">
              No skills added yet.
            </div>
          )}
        </div>
      )}

      <ProfileSkillFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editCategoryData={selectedCategoryData}
        profileId={targetId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetCategory)}
        onClose={() => setDeleteTargetCategory(null)}
        onConfirm={handleDeleteCategoryConfirm}
        title="Delete Skill Category"
        description={`Are you sure you want to remove all skills in "${deleteTargetCategory?.categoryName || "this category"}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteCategoryMutation.isPending}
      />
    </div>
  );
}
